import type { PortableTextBlock } from '@portabletext/types'
import type { ImageAsset, Slug } from '@sanity/types'
import groq from 'groq'
import { type SanityClient } from 'next-sanity'
import { client } from './sanity.client'
import { pageBuilderQuery } from '../queries/organisms/pageBuilderQuery'
import { ButtonQuery } from '../queries/atoms/ButtonQuery'

export const pagesQuery = groq`*[_type == "page" && defined(slug.current)] | order(_createdAt desc)`
export const eventsQuery = groq`*[_type == "event" && defined(slug.current)] | order(_createdAt desc)`

export async function getPages(client: SanityClient): Promise<Page[]> {
  return await client.fetch(pagesQuery)
}

export async function getEvents(client: SanityClient): Promise<Event[]> {
  return await client.fetch(eventsQuery)
}

export const pageBySlugQuery = groq`*[_type == "page" && slug.current == $slug][0]{
  ...,
  "parent": parent->{slug{...,},title},
  seoGroup {
    ...,
  },
  pageBuilder[] {
    _type == "EventType" => {
      ...,
      "events": events[]->{
        title,
        "slug": slug.current,
        date,
        "image": image.asset->url,
      },
    },
    _type == "ContactFormSection" => {
      ...,
      "heading": heading->heading,
    },
    
    _type == "hero" => {
      ...,
      "video": file.asset->url,
    },
    _type == "textWithIllustration" => {
      ...,
    },
     _type == "FaneModul" => {
      ...,
    },
    _type == "textContainer" => {
      ...,
      _type == "button" => {
        style,
        text,
        _key,
        target,
        path,
          "reference": reference->{
            _type, 
            title,
            slug
          },
      },
    },
    _type == "ThreeColumnSection" => {
      ...,
      alignment,
      innerBlocks[]{
        ...,
        _type == "button" => {
          style,
          text,
          _key,
          target,
          path,
          "reference": reference->{
            _type, 
            title,
            slug
          },
        },
      },
    },
    _type == "EmployeesType" => {
      ...,
      "positionsType": employeePosition.Stilling[]->{
        title
      },
      "employees": employees[]->{
        title,
        email,
        phone,
        "image": image.asset->url,
        "positionTitle": employeePosition.position,
      },
    },
  },
}`

export const articleBySlugQuery = groq`*[_type == "article" && slug.current == $slug][0]{
  ...,
  "image": image.asset->url,
  "parent": parent->{slug{...,},title},
  seoGroup {
    ...,
  }
}
`
export const eventBySlugQuery = groq`*[_type == "event" && slug.current == $slug][0]{
  ...,
  "image": image.asset->url,
  "parent": parent->{slug{...,},title},
  seoGroup {
    ...,
  },
}`

export async function getPage(
  client: SanityClient,
  slug: string,
): Promise<Page> {
  return await client.fetch(pageBySlugQuery, {
    slug,
  })
}
export const articleSlugsQuery = groq`
*[_type == "article" && defined(slug.current)][].slug.current`

export const eventSlugsQuery = groq`
*[_type == "event" && defined(slug.current)][].slug.current`

export const pageSlugsQuery = groq`
*[_type == "page" && defined(slug.current)][].slug.current`

export const pageSubSlugsQuery = groq`
*[_type == "page" && defined(parent.slug.current)][].parent.slug.current`

interface HeroSection {
  alignment: 'center' | 'left' | 'right'
  // TODO: add to a new file for better organization
  heading: string
  _type: 'hero'
  _key: string
  AltText: string
  tagline: string
  image: {
    _type: 'image'
    alt: string
    asset: {
      _ref: any
      _type: 'reference'
    }
  }
}

interface EmployeesSection {
  _type: 'EmployeesType'
  heading: string
  view: 'manual' | 'department'
  _ref: string
  employees: EmployeeType[]
}

interface EmployeeType {
  title: string
  email: string
  phone: string
  position: string
  image: ImageAsset
}

interface textContainer {
  _type: 'textContainer'
  _key: string
  text: string
  color: 'lys' | 'hvid'
  innerBlocks: innerBlock
}
interface innerBlock {
  map(
    arg0: (block: any, index: any) => import('react').JSX.Element,
  ): import('react').ReactNode | Iterable<import('react').ReactNode>
  _type: heading | text | accordions
  _key: string
  text: string
}
interface heading {
  _type: 'heading'
  _key: string
  text: string
}

interface text {
  _type: 'text'
  _key: string
  text: string
}
interface accordions {
  _type: 'accordions'
  accordions: AccordionItem[]
  _key: string
  text: string
}

interface AccordionItem {
  title: string
  _type: 'accordion'
  _key: string
  body: PortableTextBlock[]
}

export interface Page {
  _type: 'page'
  _id: string
  _createdAt: string
  title?: string
  parent?: {
    _ref: string
    _type: 'page'
  }
  slug: Slug
  excerpt?: string
  mainImage?: ImageAsset
  body: PortableTextBlock[]
  pageBuilder: (HeroSection | textContainer | EmployeesSection)[]
  seoGroup: Seo
}

export interface Seo {
  radioField: string
  seoDescription: string
  seoTitle: string
  seoKeywords: {
    _key: string
    _type: string
    keyword: string
  }[]
  seoImage: {
    _type: string
    asset: {
      _ref: any
      _type: string
    }
  }
}

interface pageData {
  title: string
  slug: { current: string }
  pageBuilder: HeroSection[]
  seoGroup: {
    radioField: string
    seoDescription: string
    seoKeywords: { _key: string; _type: string; keyword: string }[]
    seoTitle: string
    seoImage: { _type: string }
  }
}

interface Props {
  pageData: pageData
  page: pageData
}

// Extend this if you have additional props
interface SharedPageProps {
  draftMode: boolean
  token: string
  pageData: pageData
  pageBuilderData: any
  seoGroup: any
}

/*  */
/* Footer */
/*  */
export type FooterType = {
  _id: any
  title: any
  logo: any
  object: {
    companyName: any
    address: any
    address2: any
    telephone: any
    email: any
    cvr: any
  }
  social: {
    platform: any
    url: any
  }[]
  shortcuts: {
    link: {
      url: string
      internalUrl: string
      blank: any
      _type: any
      label: any
      type: any
      in
      internalLink: {
        _type: any
      }
    }
  }[]
}

export async function getFooter() {
  return client.fetch(
    groq`*[_type == "footer"]{
      title,
      logo {
    asset->{
      _id,
      url,
      _type,
      altText,
      description,
      title,
      metadata {
        blurHash,
        dimensions,
      }
    }
  },

      object {
        companyName,
        address,
        address2,
        telephone,
        email,
        cvr
      },
      social[] {
        platform,
        url
      },
      shortcuts[] {
    ${ButtonQuery}
      },
    }`,
  )
}

export const seoGroupQuery = groq`
seoGroup {
  "image": seoImage.asset->url,
    ...,
  }`

export const POSTS_QUERY = groq`*[_type == "page" && slug.current == '/'][0]{
  ...,
  "parent": parent->{
      slug{...,},
      title
  },
  ${seoGroupQuery},
  ${pageBuilderQuery},
}`

export const POST_QUERY = groq`*[_type == "page" && slug.current == $slug][0]{
  title, body, mainImage
}`

export const pagesBySlugQuery = groq`
  *[_type == "page" && slug.current == $slug][0]{
    ...,
    "isFirstPageTitle": *[_type == "page" && slug.current == $slug][0].pageBuilder[0]._type == "PageTitle",

    "parent": parent->{
        slug{...,},
        title
    },
    
    ${seoGroupQuery},
    ${pageBuilderQuery},
}`


export const JOB_PAGE_QUERY = groq`
  *[_type == "job" && slug.current == $slug][0]{
    ...,
    ${seoGroupQuery},
    ${pageBuilderQuery},
}`


export const PAGE_QUERY = groq`*[_type == "page" && slug.current == $slug][0]{
  ...,
  "parent": parent->{
      slug{...,},
      title
  },
  "isFirstPageTitle": *[_type == "page" && slug.current == $slug][0].pageBuilder[0]._type == "PageTitle",
  ${seoGroupQuery},
  ${pageBuilderQuery},
}`